import React, { useEffect, useState } from 'react';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody,
  DrawerFooter, DrawerCloseButton, Box, Button, Flex, Text, Avatar,
  HStack, Spinner, VStack, Divider, Tag, Wrap, WrapItem, useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../utils/axio-instance';
import { fetchVendorReviews } from '../../actions/review.actions';
import StarRating from '../reviews/StarRating';
import VerifiedBadge from '../reviews/VerifiedBadge';
import { buildImageUrl } from '../../utils/imageUrl';

const RECENT_REVIEWS = 5;

// "mortgage_lender" -> "Mortgage Lender"
const humanize = (s) =>
  String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/**
 * VendorDetailDrawer — right-side drawer with a professional's public details:
 * name, verified badge, rating, experience, closed deals, about, and the
 * most recent reviews. No private contact info (email/phone) is shown.
 * When a requestId is given, a "Send Invite" action manually invites this
 * professional to respond to the request (email first, SMS fallback).
 *
 * Props: vendor (matched-vendors item), requestId (optional), isOpen, onClose.
 */
const VendorDetailDrawer = ({ vendor, requestId, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const reviewsState = useSelector((state) => state.reviews);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  useEffect(() => {
    if (isOpen && vendor?.userId) {
      setInviteSent(false);
      dispatch(fetchVendorReviews(vendor.userId, 1, RECENT_REVIEWS));
    }
  }, [dispatch, isOpen, vendor]);

  if (!vendor) return null;

  const handleSendInvite = async () => {
    setIsInviting(true);
    try {
      const response = await axiosInstance.post('/property/requestinvite/manual', {
        inviteType: 'any',
        requestId,
        selectedIds: [vendor.userId],
      });
      if (response.data?.savedCount > 0) {
        setInviteSent(true);
        toast({
          title: 'Invitation sent',
          description: `${vendor.firstName} ${vendor.lastName} has been invited to respond to your request.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        const reason = response.data?.emailResponses?.[0];
        toast({
          title: 'Invitation not sent',
          description: reason || 'This professional may have already been invited.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to send invitation. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsInviting(false);
    }
  };

  const vendorReviews =
    String(reviewsState.vendorId) === String(vendor.userId)
      ? reviewsState.items.slice(0, RECENT_REVIEWS)
      : [];

  const hasRating = Number(vendor.averageRating) > 0 || Number(vendor.reviewCount) > 0;
  const hasExperience = Number(vendor.experience) > 0;
  const hasClosedDeals = Number(vendor.closedDeals) > 0;
  // Services: prefer license serviceTypes, fall back to roles.
  const serviceItems = (vendor.licenses || [])
    .map((l) => l?.serviceType)
    .filter(Boolean);
  const services = serviceItems.length ? serviceItems : (vendor.roles || []);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent maxWidth={{ base: '100%', md: '440px' }}>
        <DrawerCloseButton />
        <DrawerHeader>Professional details</DrawerHeader>
        <DrawerBody>
          {/* Header card */}
          <Flex align="center" gap={4} mb={4}>
            <Avatar
              size="lg"
              name={`${vendor.firstName || ''} ${vendor.lastName || ''}`}
              src={buildImageUrl(vendor.imageUrl)}
            />
            <Box>
              <HStack spacing={2}>
                <Text fontWeight="bold" fontSize="lg">
                  {vendor.firstName} {vendor.lastName}
                </Text>
                <VerifiedBadge isVerified={vendor.isVerified} />
              </HStack>
              {hasRating && (
                <HStack spacing={1}>
                  <StarRating value={vendor.averageRating} size={4} />
                  <Text fontSize="sm" color="gray.600">
                    {Number(vendor.averageRating || 0).toFixed(1)} ({vendor.reviewCount || 0} review{vendor.reviewCount === 1 ? '' : 's'})
                  </Text>
                </HStack>
              )}
              <Text fontSize="sm" color="gray.500">
                {[vendor.companyName, vendor.city, vendor.state]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            </Box>
          </Flex>

          {/* Public stats — only when present */}
          {(hasExperience || hasClosedDeals) && (
            <HStack spacing={6} mb={4}>
              {hasExperience && (
                <Box>
                  <Text fontSize="xs" color="gray.500">Experience</Text>
                  <Text fontWeight="semibold">
                    {`${vendor.experience} year${vendor.experience > 1 ? 's' : ''}`}
                  </Text>
                </Box>
              )}
              {hasClosedDeals && (
                <Box>
                  <Text fontSize="xs" color="gray.500">Closed deals</Text>
                  <Text fontWeight="semibold">{vendor.closedDeals}</Text>
                </Box>
              )}
            </HStack>
          )}

          {vendor.aboutMe && (
            <Box mb={4}>
              <Text fontSize="xs" color="gray.500" mb={1}>About</Text>
              <Text fontSize="sm" whiteSpace="pre-wrap">{vendor.aboutMe}</Text>
            </Box>
          )}

          {services.length > 0 && (
            <Box mb={4}>
              <Text fontSize="xs" color="gray.500" mb={2}>Services</Text>
              <Wrap spacing={2}>
                {services.map((svc, i) => (
                  <WrapItem key={`${svc}-${i}`}>
                    <Tag size="sm" colorScheme="blue" borderRadius="full">{humanize(svc)}</Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {/* Recent reviews — only when the vendor has reviews */}
          {(reviewsState.loading || vendorReviews.length > 0) && <Divider mb={4} />}
          {(reviewsState.loading || vendorReviews.length > 0) && (
            <Text fontWeight="semibold" mb={2}>
              Recent reviews
            </Text>
          )}
          {reviewsState.loading && (
            <Flex justify="center" py={4}>
              <Spinner size="md" />
            </Flex>
          )}
          <VStack align="stretch" spacing={3} pb={6}>
            {vendorReviews.map((review) => (
              <Box key={review.reviewId} p={3} borderWidth="1px" borderRadius="md">
                <HStack justify="space-between" mb={1}>
                  <StarRating value={review.rating} size={3.5} />
                  <Text fontSize="xs" color="gray.500">
                    {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : ''}
                  </Text>
                </HStack>
                {review.title && (
                  <Text fontWeight="semibold" fontSize="sm" mb={1}>{review.title}</Text>
                )}
                <Text fontSize="sm" whiteSpace="pre-wrap">{review.comment}</Text>
              </Box>
            ))}
          </VStack>
        </DrawerBody>
        {requestId && (
          <DrawerFooter borderTopWidth="1px">
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSendInvite}
              isLoading={isInviting}
              loadingText="Sending"
              isDisabled={inviteSent}
            >
              {inviteSent ? 'Invite Sent' : 'Send Invite'}
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default VendorDetailDrawer;
